import { AxiosError } from 'axios'
import React from 'react'
import { objectKeys } from 'ts-extras'

import {
  API_PATH_INTERNAL,
  REFERRAL_CONSENT_LETTER_VARIANT,
  REFERRAL_CONSENT_STAGE,
  REFERRAL_STATUS_ID,
} from '@/lib/constants'
import apiService from '@/lib/services/apiService'
import commercialAgreementService from '@/lib/services/commercialAgreementService'
import errorTypeService from '@/lib/services/errorTypeService'
import legislationService from '@/lib/services/legislationService'
import { DisabledReason } from '@/pages/referClient/steps/2_ReferralDetails/components/SelectServiceFirm/constants'
import { ReferralsTab } from '@/pages/referrals/constants'
import { CommercialAgreementTypeOption } from '@/redux/slices/commercialAgreementsForm'
import {
  ReferralAccessDeniedReason,
  ReferralConsentLetterVariant,
  ReferralConsentStage,
} from '@/types/misc'
import {
  ForwardReferralApprovalRequest,
  UpdateReferralApprovalStatusRequest,
  UpdateReferralClientRequest,
} from '@/types/requests/clients'
import {
  MeetingRequest,
  RescheduleMeetingRequest,
} from '@/types/requests/meetings'
import {
  ArchiveReferralsRequest,
  CreateClientReferralRequest,
  CreateDirectLeadReferralRequest,
  CreateHistoricalReferralRequest,
  CreateReferralEmailPreviewRequest,
  CreateReferralESignatureConsentPreviewRequest,
  CreateReferralInviteRequest,
  CreateReferralRequest,
  CreateReferralViaReferralLinkRequest,
  GetReferralFirmsRequestParams,
  GetReferralIndividualsRequestParams,
  GetReferralInvitesRequestParams,
  GetReferralServiceAreaRequestParams,
  GetReferralWithESignatureDetailsParams,
  LogDirectLeadReferralRequest,
  ReferralCollectionParams,
  RequestReferralCallbackRequest,
  SubmitESignatureConsentLetterRequest,
  SubmitRetrospectiveESignatureConsentLetterRequest,
  UnarchiveReferralsRequest,
  UpdateReferralRequest,
  UpdateReferralServicesRequest,
  UpdateReferralToCompanyRequest,
} from '@/types/requests/referrals'
import { RescheduledMeetingItem } from '@/types/responses/meetings'
import {
  AffiliateLinkCollectionResponse,
  ApprovalRequestsMetadataItem,
  ArchiveReferralsResponse,
  CreateClientReferralResponse,
  CreateReferralResponse,
  CreateReferralViaReferralLinkResponse,
  LogDirectLeadReferralResponse,
  ReferralCollection,
  ReferralCollectionItem,
  ReferralCompanyTypeCollection,
  ReferralEmailItem,
  ReferralFirmCollection,
  ReferralGuestItem,
  ReferralGuestRescheduleMeetingItem,
  ReferralIndividualCollection,
  ReferralInviteCollection,
  ReferralInviteItem,
  ReferralItem,
  ReferralServiceAreaCollection,
  ReferralSetupDataItem,
  ReferralsTableMetadataItem,
  ReferralSummary,
  ReferralWithESignatureDetailsItem,
  SubmitESignatureConsentLetterResponse,
  SubmitRetrospectiveESignatureConsentLetterResponse,
  UnarchiveReferralsResponse,
  WithdrawGdprConsentForReferralResponse,
} from '@/types/responses/referrals'

export class ReferralService {
  private readonly endpoint = `${API_PATH_INTERNAL}/referrals`

  async getReferrals(
    filters: ReferralCollectionParams,
  ): Promise<ReferralCollection> {
    const response = await apiService.get(this.endpoint, {
      params: filters,
    })

    return response.data
  }

  /**
   * Fetch *every* referral matching the given params (across all pages), used
   * by the CSV export. The API caps the page size, so we derive the real page
   * size from the first response and fetch the remaining pages sequentially
   * (to avoid overwhelming the API). Pages are reassembled in order so the
   * result matches the table.
   */
  async getAllReferrals(
    params: ReferralCollectionParams,
    options?: {
      onProgress?: (completedPages: number, totalPages: number) => void
    },
  ): Promise<ReferralCollection> {
    const pageSize = 100

    const initialResponse = await this.getReferrals({
      ...params,
      page: 1,
      pageSize,
    })

    const totalItems = initialResponse['hydra:totalItems']
    // The API may clamp pageSize below what we asked for, so use the actual
    // number of rows returned to work out how many pages there really are.
    const effectivePageSize = initialResponse['hydra:member'].length

    if (effectivePageSize === 0 || totalItems <= effectivePageSize) {
      options?.onProgress?.(1, 1)
      return initialResponse
    }

    const totalPages = Math.ceil(totalItems / effectivePageSize)
    options?.onProgress?.(1, totalPages)

    const members: ReferralCollectionItem[] = [
      ...initialResponse['hydra:member'],
    ]

    for (let page = 2; page <= totalPages; page++) {
      const response = await this.getReferrals({ ...params, page, pageSize })
      members.push(...response['hydra:member'])
      options?.onProgress?.(page, totalPages)
    }

    return {
      ...initialResponse,
      'hydra:member': members,
    }
  }

  async getReferralsTableMetadata(
    tab: ReferralsTab,
    companyId?: number,
    groupId?: number,
  ): Promise<ReferralsTableMetadataItem> {
    const response = await apiService.get<ReferralsTableMetadataItem>(
      `${this.endpoint}/table-metadata`,
      { params: { companyId, direction: tab, groupId } },
    )

    return response.data
  }

  async getReferralSummary(
    referralId: number,
    token?: string,
  ): Promise<ReferralSummary> {
    const referral = token
      ? await this.getReferralInvite(referralId, { token })
      : await this.getReferral(referralId)

    const hasReferredForFcaService = referral.services.some(
      (service) => service['@type'] === 'fca-service',
    )

    // If referral is not for an FCA service, there's no need to
    // fetch legislations
    if (!hasReferredForFcaService) {
      return {
        referral,
        legislations: [],
      }
    }

    // Otherwise, let's fetch the legislations
    const serviceAreaIds = referral.services.map(
      (service) => service.serviceArea.id,
    )
    const legislationsData =
      await legislationService.getLegislationsByFcaServiceAreaIds(
        serviceAreaIds,
      )

    return { referral, legislations: legislationsData['hydra:member'] }
  }

  async getReferral(referralId: number): Promise<ReferralItem> {
    const response = await apiService.get(`${this.endpoint}/${referralId}`)

    return response.data
  }

  async getReferralInvite(
    referralId: number,
    params: {
      token: string
    },
  ): Promise<ReferralInviteItem> {
    const response = await apiService.get(
      `${this.endpoint}/invites/${referralId}`,
      { params },
    )

    return response.data
  }

  async getReferralInvites(
    params: GetReferralInvitesRequestParams,
  ): Promise<ReferralInviteCollection> {
    const response = await apiService.get<ReferralInviteCollection>(
      `${this.endpoint}/invites`,
      { params },
    )

    return response.data
  }

  async getReferralWithESignatureDetails(
    referralId: number,
    params: GetReferralWithESignatureDetailsParams,
  ): Promise<ReferralWithESignatureDetailsItem> {
    const response = await apiService.get(
      `${this.endpoint}/${referralId}/guest/with-esignature-details`,
      { params },
    )

    return response.data
  }

  async createReferralESignatureConsentPreview(
    request: CreateReferralESignatureConsentPreviewRequest,
  ): Promise<ReferralWithESignatureDetailsItem> {
    const response = await apiService.post(
      `${this.endpoint}/guest/esignature-consent-letter/preview`,
      request,
    )

    return response.data
  }

  async getReferralByCallbackCode(
    referralId: number,
    callbackCode: string,
  ): Promise<ReferralGuestItem> {
    const response = await apiService.get<ReferralGuestItem>(
      `${this.endpoint}/${referralId}/guest`,
      { params: { callbackCode } },
    )

    return response.data
  }

  async getReferralByWithdrawGdprConsentCode(
    referralId: number,
    withdrawGdprConsentCode: string,
  ): Promise<ReferralGuestItem> {
    const response = await apiService.get<ReferralGuestItem>(
      `${this.endpoint}/${referralId}/guest`,
      { params: { withdrawGdprConsentCode } },
    )

    return response.data
  }

  async createReferral(
    request:
      | CreateReferralRequest
      | CreateReferralInviteRequest
      | CreateHistoricalReferralRequest,
  ): Promise<CreateReferralResponse> {
    const response = await apiService.post(this.endpoint, request)

    return response.data
  }

  async confirmClientConsentForReferral(
    referralId: number,
    callbackCode: string,
  ): Promise<ReferralGuestItem> {
    const response = await apiService.patch<ReferralGuestItem>(
      `${this.endpoint}/${referralId}/guest`,
      {
        status:
          `${API_PATH_INTERNAL}/referral-statuses/` +
          REFERRAL_STATUS_ID.AWAITING_ADVISER_TO_CONTACT_CLIENT,
      },
      { params: { callbackCode } },
    )

    return response.data
  }

  async requestReferralCallback(
    referralId: number,
    callbackCode: string,
    request: RequestReferralCallbackRequest,
  ): Promise<ReferralItem> {
    const response = await apiService.patch<ReferralItem>(
      `${this.endpoint}/${referralId}/guest`,
      request,
      { params: { callbackCode } },
    )

    return response.data
  }

  async createReferralEmailPreview(
    request: CreateReferralEmailPreviewRequest,
  ): Promise<ReferralEmailItem> {
    const response = await apiService.post(
      `${this.endpoint}/email-preview`,
      request,
    )

    return response.data
  }

  async provideGdprConsentForReferral(referralId: number) {
    const response = await apiService.patch(`${this.endpoint}/${referralId}`, {
      hasGdprConsent: true,
    })

    return response.data
  }

  async withdrawGdprConsentForReferral(
    referralId: number,
    withdrawConsentCode: string,
  ): Promise<WithdrawGdprConsentForReferralResponse> {
    const response = await apiService.patch(
      `${this.endpoint}/${referralId}/guest/withdraw-gdpr-consent`,
      { hasGdprConsent: false },
      { params: { withdrawGdprConsentCode: withdrawConsentCode } },
    )

    return response.data
  }

  async updateReferralStatus(
    referralId: number,
    newStatusIri: IRI,
  ): Promise<ReferralItem> {
    const response = await apiService.patch<ReferralItem>(
      `${this.endpoint}/${referralId}/status`,
      {
        mappedStatus: newStatusIri,
      },
    )

    return response.data
  }

  async guestAddMeeting(
    referralId: number,
    callbackCode: string,
    meeting: MeetingRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${referralId}/guest/add-meeting`,
      meeting,
      { params: { callbackCode } },
    )

    return response.data
  }

  async rescheduleMeeting(
    referralId: number,
    request: RescheduleMeetingRequest,
  ): Promise<RescheduledMeetingItem> {
    const response = await apiService.post<RescheduledMeetingItem>(
      `${this.endpoint}/${referralId}/reschedule-meeting`,
      request,
    )

    return response.data
  }

  async guestGetRescheduleMeeting(
    referralId: number,
    callbackCode: string,
  ): Promise<ReferralGuestRescheduleMeetingItem> {
    const response = await apiService.get<ReferralGuestRescheduleMeetingItem>(
      `${this.endpoint}/${referralId}/guest/reschedule-meeting`,
      { params: { callbackCode } },
    )

    return response.data
  }

  async guestRescheduleMeeting(
    referralId: number,
    callbackCode: string,
    request: RescheduleMeetingRequest,
  ): Promise<RescheduledMeetingItem> {
    const response = await apiService.patch<RescheduledMeetingItem>(
      `${this.endpoint}/${referralId}/guest/reschedule-meeting`,
      request,
      { params: { callbackCode } },
    )

    return response.data
  }

  async updateReferralReason(
    referralId: number,
    reason: string,
  ): Promise<unknown> {
    return this.updateReferral(referralId, { reason })
  }

  async updateReferral(
    referralId: number,
    request: UpdateReferralRequest,
  ): Promise<unknown> {
    const response = await apiService.patch(
      `${this.endpoint}/${referralId}`,
      request,
    )

    return response.data
  }

  async attachCompassReportToReferral(
    referralId: number,
    compassReportIri: IRI,
  ) {
    const response = await apiService.patch(`${this.endpoint}/${referralId}`, {
      compassReport: compassReportIri,
    })

    return response.data
  }

  async unattachCompassReportFromReferral(referralId: number) {
    const response = await apiService.patch<ReferralItem>(
      `${this.endpoint}/${referralId}`,
      { compassReport: null },
    )

    return response.data
  }

  async updateReferralToCompany(
    referralId: number,
    request: UpdateReferralToCompanyRequest,
  ) {
    const response = await apiService.patch<ReferralItem>(
      `${this.endpoint}/${referralId}/to-company`,
      request,
    )

    return response.data
  }

  async updateReferralIntroducedByUser(
    referralId: number,
    user: IRI,
  ): Promise<unknown> {
    const response = await apiService.patch(
      `${this.endpoint}/${referralId}/introduced-by-user`,
      { user },
    )

    return response.data
  }

  async updateReferralIntroducedToUsers(
    referralId: number,
    userIds: number[],
  ): Promise<unknown> {
    const response = await apiService.patch(
      `${this.endpoint}/${referralId}/introduced-to-users`,
      { userIds },
    )

    return response.data
  }

  async updateReferralClient(
    referralId: number,
    request: UpdateReferralClientRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${referralId}/update-client`,
      request,
    )

    return response.data
  }

  async updateReferralClientEmail(
    referralId: number,
    hasPausedClientEmails: boolean,
  ) {
    const response = await apiService.patch(`${this.endpoint}/${referralId}`, {
      hasPausedClientEmails,
    })

    return response.data
  }

  async updateReferralServices(
    referralId: number,
    request: UpdateReferralServicesRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${referralId}/services`,
      request,
    )

    return response.data
  }

  async updateReferralApprovalStatus(
    referralId: number,
    request: UpdateReferralApprovalStatusRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${referralId}/approval-status`,
      request,
    )

    return response.data
  }

  async forwardApprovalRequest(
    referralId: number,
    request: ForwardReferralApprovalRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${referralId}/forward-approval-request`,
      request,
    )

    return response.data
  }

  async submitESignatureConsentLetter(params: {
    referralId: number
    callbackCode: string
    request: SubmitESignatureConsentLetterRequest
  }): Promise<SubmitESignatureConsentLetterResponse> {
    const { referralId, request, callbackCode } = params

    const response = await apiService.post(
      `${this.endpoint}/${referralId}/guest/esignature-consent-letter/submit`,
      request,
      { params: { callbackCode } },
    )

    return response.data
  }

  async submitRetrospectiveESignatureConsentLetter(params: {
    referralId: number
    callbackCode: string
    request: SubmitRetrospectiveESignatureConsentLetterRequest
  }): Promise<SubmitRetrospectiveESignatureConsentLetterResponse> {
    const { referralId, request, callbackCode } = params

    const response = await apiService.post(
      `${this.endpoint}/${referralId}/guest/retrospective-esignature-consent-letter/submit`,
      request,
      { params: { callbackCode } },
    )

    return response.data
  }

  async getReferralIndividuals(
    params: GetReferralIndividualsRequestParams,
  ): Promise<ReferralIndividualCollection> {
    const response = await apiService.get(
      `${this.endpoint}/by-firm-individuals`,
      { params },
    )

    return response.data
  }

  async getReferralFirms(
    params: GetReferralFirmsRequestParams,
  ): Promise<ReferralFirmCollection> {
    const response = await apiService.get(`${this.endpoint}/by-firms`, {
      params,
    })

    return response.data
  }

  async getReferralServiceAreas(
    params?: GetReferralServiceAreaRequestParams,
  ): Promise<ReferralServiceAreaCollection> {
    const response = await apiService.get(`${this.endpoint}/by-service-areas`, {
      params,
    })

    return response.data
  }

  async getReferralCompanyTypes(): Promise<ReferralCompanyTypeCollection> {
    const response = await apiService.get(`${this.endpoint}/by-company-types`)

    return response.data
  }

  /**
   * The receiving firm confirms the client permits their details being shared back to the
   * introducing firm, which lifts the withholding applied at import (DEV-541).
   */
  async liftClientRedaction(referralId: number): Promise<ReferralItem> {
    const response = await apiService.post(
      `${this.endpoint}/${referralId}/lift-client-redaction`,
      {},
    )

    return response.data
  }

  async remindReferralApprovalRequests(referralId: number): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/${referralId}/remind-approval-requests`,
      {},
    )

    return response.data
  }

  async resendReferralConsent(referralId: number): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/${referralId}/resend-consent`,
      {},
    )

    return response.data
  }

  async resendReferralInvite(referralId: number): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/${referralId}/resend-invite`,
      {},
    )

    return response.data
  }

  shouldShowCommissionJustification(
    agreementType: CommercialAgreementTypeOption | undefined,
  ): boolean {
    return commercialAgreementService.isFeeSharingAgreement(agreementType)
  }

  async createReferralViaReferralLink(
    request: CreateReferralViaReferralLinkRequest,
  ): Promise<CreateReferralViaReferralLinkResponse> {
    const response = await apiService.post(
      `${this.endpoint}/guest/create-via-referral-link`,
      request,
    )

    return response.data
  }

  async createDirectLeadReferral(
    request: CreateDirectLeadReferralRequest,
  ): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/guest/create-direct-lead-referral`,
      request,
    )

    return response.data
  }

  async logDirectLeadReferral(
    request: LogDirectLeadReferralRequest,
  ): Promise<LogDirectLeadReferralResponse> {
    const response = await apiService.post(
      `${this.endpoint}/log-direct-lead-referral`,
      request,
    )

    return response.data
  }

  async createClientReferral(
    request: CreateClientReferralRequest,
  ): Promise<CreateClientReferralResponse> {
    const response = await apiService.post(
      `${this.endpoint}/guest/create-client-referral`,
      request,
    )

    return response.data
  }

  async getReferralSetupData(): Promise<ReferralSetupDataItem> {
    const response = await apiService.get(`${this.endpoint}/setup-data`)

    return response.data
  }

  async getApprovalRequestsMetadata(): Promise<ApprovalRequestsMetadataItem> {
    const response = await apiService.get(
      `${this.endpoint}/approval-requests-metadata`,
    )

    return response.data
  }

  async archiveReferrals(
    request: ArchiveReferralsRequest,
  ): Promise<ArchiveReferralsResponse> {
    const response = await apiService.post(`${this.endpoint}/archive`, request)

    return response.data
  }

  async unarchiveReferrals(
    request: UnarchiveReferralsRequest,
  ): Promise<UnarchiveReferralsResponse> {
    const response = await apiService.post(
      `${this.endpoint}/unarchive`,
      request,
    )

    return response.data
  }

  async getAffiliateLinks(
    referralClientCode: string,
  ): Promise<AffiliateLinkCollectionResponse> {
    const response = await apiService.get(
      `${this.endpoint}/${referralClientCode}/guest/affiliate-links`,
    )

    return response.data
  }

  async recordAffiliateLinkVisit(
    referralClientCode: string,
    serviceIds: number[],
  ) {
    await apiService.post(
      `${this.endpoint}/${referralClientCode}/guest/affiliate-links/record-visit`,
      {
        serviceIds,
      },
    )
  }

  isReferralAccessDeniedError(error: unknown): error is AxiosError<{
    reason: ReferralAccessDeniedReason
  }> {
    return (
      errorTypeService.isForbiddenError(error) &&
      error instanceof AxiosError &&
      !!error.response?.data &&
      typeof error.response.data === 'object' &&
      'reason' in error.response.data
    )
  }

  async integrationReprocessReferral(referralId: number) {
    await apiService.post(
      `${this.endpoint}/${referralId}/integration-reprocess`,
      {},
    )
  }

  async setIntegrationSource(
    referralId: number,
    body: {
      opportunityId?: number | null
      clientId?: number | null
      leadId?: number | null
    },
  ): Promise<void> {
    await apiService.post(
      `${this.endpoint}/${referralId}/integration-source`,
      body,
    )
  }

  getFirmDisabledMessage({
    companyName,
    disabledReason,
  }: {
    companyName: string
    disabledReason: DisabledReason
  }): React.ReactElement | null {
    const disabledMessages: Record<DisabledReason, React.ReactElement | null> =
      {
        missingServiceAndUnapproved: (
          <>
            {companyName} currently doesn't offer the selected services nor are
            they in your approved professional network
          </>
        ),
        unapproved: (
          <>
            The relationship with {companyName} is still pending approval. You
            can start referring to them once the relationship is approved.
          </>
        ),
        missingService: (
          <>{companyName} currently doesn’t offer the selected services</>
        ),
        nonStandaloneServicesHaveFinancialPlanSelected: null,
        fcaFirmRestrictedService: null,
      }

    return disabledMessages[disabledReason]
  }

  isValidConsentStage(
    consentStage: string,
  ): consentStage is ReferralConsentStage {
    return objectKeys(REFERRAL_CONSENT_STAGE).includes(
      consentStage as ReferralConsentStage,
    )
  }

  isValidConsentLetterVariant(
    consentLetterVariant: string,
  ): consentLetterVariant is ReferralConsentLetterVariant {
    return objectKeys(REFERRAL_CONSENT_LETTER_VARIANT).includes(
      consentLetterVariant as ReferralConsentLetterVariant,
    )
  }
}

const referralService = new ReferralService()

export default referralService
